import { create } from 'zustand';

import { db } from '@/services/storage/db';
import { TaskService } from '@/services/task-service';
import { ChecklistEngine } from '@/services/checklist-engine';
import { posthogService } from '@/services/posthog-service';
import { EVENT_TASK_COMPLETED, EVENT_TASK_SNOOZED } from '@/services/observability/events';
import type { Task, CreateTaskInput } from '@/types/task';
import type { Season } from '@/types/season';
import type { Property } from '@/types/property';
import type { ChecklistTemplate } from '@/types/checklist-template';

const taskService = new TaskService(db);

interface TaskStore {
  tasks: Task[];
  /** Month-scoped tasks for off-season prep widget (M64). Separate slice from tasks to avoid
   *  disrupting NextAction/UpcomingTasks which read from `tasks`. */
  tasksDueThisMonth: Task[];
  /** Monotonic mutation counter — bumped on every task mutation. DashboardPage subscribes to
   *  this to invalidate its getDashboardData() snapshot when tasks mutate out-of-band (the
   *  residual gap liveTaskStats cannot cover — e.g. an out-of-band addTask changing a season's
   *  total denominator). Mirrors the O-1 refreshKey-invalidation pattern at DashboardPage.tsx:179-190
   *  (defect: dashboardpage-stale-snapshot-body-consumers.md). Invariant: every task mutator bumps
   *  revision — including completeTask/optimisticallyRemoveTask which liveTaskStats already covers;
   *  the redundant bump is harmless because revision only triggers a getDashboardData re-fetch
   *  (no double-counting), and a single invariant is simpler to reason about. */
  revision: number;
  isLoading: boolean;
  error: string | null;
  loadTasks: (seasonId: string) => Promise<void>;
  loadUpcomingTasks: (limit?: number) => Promise<void>;
  /** Loads tasks due in the current calendar month into tasksDueThisMonth slice. */
  loadTasksDueThisMonth: () => Promise<void>;
  /** Optimistically removes a task from local state without touching isLoading.
   *  Splices from BOTH tasks AND tasksDueThisMonth so quick-complete propagates to all widgets. */
  optimisticallyRemoveTask: (id: string) => void;
  /** Refreshes upcoming tasks without toggling isLoading (silent reconcile) */
  reloadUpcomingTasksSilent: (limit?: number) => Promise<void>;
  completeTask: (id: string) => Promise<void>;
  uncompleteTask: (id: string) => Promise<void>;
  addTasksFromTemplate: (
    template: ChecklistTemplate,
    season: Season,
    property?: Property,
  ) => Promise<void>;
  generateFromTemplate: (templateId: string, seasonId: string, propertyId?: string) => Promise<void>;
  addTask: (data: CreateTaskInput) => Promise<void>;
  editTask: (
    id: string,
    title: string,
    description: string,
    dueDate: string,
    propertyId: string | undefined,
    waypointId: string | undefined,
  ) => Promise<void>;
  updateTask: (id: string, data: import('@/types/task').UpdateTaskInput) => Promise<void>;
  removeTask: (id: string) => Promise<void>;
  reorderTask: (id: string, direction: 'up' | 'down') => Promise<void>;
  snoozeTask: (id: string, days: number) => Promise<void>;
  snoozeTaskUntil: (id: string, until: Date) => Promise<void>;
  unsnoozeTask: (id: string) => Promise<void>;
}

export const useTaskStore = create<TaskStore>((set, get) => ({
  tasks: [],
  tasksDueThisMonth: [],
  revision: 0,
  isLoading: false,
  error: null,

  loadTasks: async (seasonId) => {
    set({ isLoading: true, error: null });
    try {
      const tasks = await taskService.getTasks(seasonId);
      set({ tasks, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load tasks';
      console.error('TaskStore.loadTasks:', err);
      set({ error: message, isLoading: false });
    }
  },

  loadUpcomingTasks: async (limit) => {
    set({ isLoading: true, error: null });
    try {
      const tasks = await taskService.getUpcomingTasks(limit);
      set({ tasks, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load upcoming tasks';
      console.error('TaskStore.loadUpcomingTasks:', err);
      set({ error: message, isLoading: false });
    }
  },

  loadTasksDueThisMonth: async () => {
    try {
      const tasks = await taskService.getTasksDueThisMonth();
      set({ tasksDueThisMonth: tasks });
    } catch (err) {
      console.error('TaskStore.loadTasksDueThisMonth:', err);
      // Silent — don't surface loading error; widget simply stays empty
    }
  },

  optimisticallyRemoveTask: (id) => {
    // Splice from BOTH slices so quick-complete propagates to OffSeasonPrepWidget too
    set((state) => ({
      tasks: state.tasks.filter((t) => t.id !== id),
      tasksDueThisMonth: state.tasksDueThisMonth.filter((t) => t.id !== id),
      revision: state.revision + 1,
    }));
  },

  reloadUpcomingTasksSilent: async (limit) => {
    try {
      const tasks = await taskService.getUpcomingTasks(limit);
      set({ tasks });
    } catch (err) {
      console.error('TaskStore.reloadUpcomingTasksSilent:', err);
      // Silent — don't surface error to UI, just log it
    }
  },

  completeTask: async (id) => {
    try {
      const updated = await taskService.completeTask(id);
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === id ? updated : t)),
        revision: state.revision + 1,
      }));
      posthogService.track(EVENT_TASK_COMPLETED);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to complete task';
      console.error('TaskStore.completeTask:', err);
      set({ error: message });
      throw err;
    }
  },

  uncompleteTask: async (id) => {
    try {
      const updated = await taskService.uncompleteTask(id);
      // Recompute blocked state so any dependents that lost a completed predecessor get re-blocked
      await taskService.recomputeBlockedState(updated.seasonId);
      const allTasks = await taskService.getTasks(updated.seasonId);
      set((state) => ({ tasks: allTasks, revision: state.revision + 1 }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to uncomplete task';
      console.error('TaskStore.uncompleteTask:', err);
      set({ error: message });
      throw err;
    }
  },

  addTasksFromTemplate: async (template, season, property) => {
    set({ isLoading: true, error: null });
    try {
      // Guard: skip generation if tasks already exist for this season
      const existing = await taskService.getTasks(season.id);
      if (existing.length > 0) {
        set({ tasks: existing, isLoading: false });
        return;
      }
      const engine = new ChecklistEngine(taskService);
      await engine.generateFromTemplate(template, season, property);
      const tasks = await taskService.getTasks(season.id);
      set({ tasks, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate tasks from template';
      console.error('TaskStore.addTasksFromTemplate:', err);
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  generateFromTemplate: async (templateId, seasonId, propertyId) => {
    set({ isLoading: true, error: null });
    try {
      const [template, season] = await Promise.all([
        db.getTemplate(templateId),
        db.getSeason(seasonId),
      ]);
      if (!template) throw new Error(`Template not found: ${templateId}`);
      if (!season) throw new Error(`Season not found: ${seasonId}`);

      const property = propertyId ? await db.getProperty(propertyId) : null;

      // Guard: skip generation if tasks already exist for this season
      const existing = await taskService.getTasks(seasonId);
      if (existing.length > 0) {
        set({ tasks: existing, isLoading: false });
        return;
      }

      const engine = new ChecklistEngine(taskService);
      await engine.generateFromTemplate(template, season, property ?? undefined);
      const tasks = await taskService.getTasks(seasonId);
      set({ tasks, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate tasks';
      console.error('TaskStore.generateFromTemplate:', err);
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  addTask: async (data) => {
    try {
      const created = await taskService.createTask(data);
      set((state) => ({
        tasks: [...state.tasks, created].sort((a, b) => a.sortOrder - b.sortOrder),
        revision: state.revision + 1,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create task';
      console.error('TaskStore.addTask:', err);
      set({ error: message });
      throw err;
    }
  },

  editTask: async (id, title, description, dueDate, propertyId, waypointId) => {
    try {
      const updated = await taskService.updateTask(id, {
        title,
        description,
        dueDate,
        propertyId,
        waypointId,
      });
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === id ? updated : t)),
        revision: state.revision + 1,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update task';
      console.error('TaskStore.editTask:', err);
      set({ error: message });
      throw err;
    }
  },

  updateTask: async (id, data) => {
    try {
      const updated = await taskService.updateTask(id, data);
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === id ? updated : t)),
        revision: state.revision + 1,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update task';
      console.error('TaskStore.updateTask:', err);
      set({ error: message });
      throw err;
    }
  },

  removeTask: async (id) => {
    const { tasks } = get();
    const task = tasks.find((t) => t.id === id);
    try {
      await taskService.deleteTask(id);
      // Remove from state first
      set((state) => ({ tasks: state.tasks.filter((t) => t.id !== id), revision: state.revision + 1 }));
      // Unblock dependents: removed dep is treated as satisfied
      if (task) {
        const dependents = tasks.filter((t) => (t.dependsOn ?? []).includes(id) && t.id !== id);
        for (const dep of dependents) {
          const newDependsOn = (dep.dependsOn ?? []).filter((depId) => depId !== id);
          const shouldBeBlocked = newDependsOn.some(
            (depId) => tasks.find((t) => t.id === depId && !t.completed),
          );
          await taskService.updateTask(dep.id, {
            dependsOn: newDependsOn,
            blocked: shouldBeBlocked,
          });
        }
        // Reload to reflect updated blocked state
        const refreshed = await taskService.getTasks(task.seasonId);
        // Note: removeTask already bumped revision once above (the optimistic filter set).
        // The blocked-dependent reconciliation re-set does NOT bump again — a single removeTask
        // call is one logical mutation; one bump is sufficient to trigger a snapshot re-fetch.
        set({ tasks: refreshed.filter((t) => t.id !== id) });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete task';
      console.error('TaskStore.removeTask:', err);
      set({ error: message });
      throw err;
    }
  },

  reorderTask: async (id, direction) => {
    const { tasks } = get();
    const sorted = [...tasks].sort((a, b) => a.sortOrder - b.sortOrder);
    const index = sorted.findIndex((t) => t.id === id);
    if (index === -1) return;

    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= sorted.length) return;

    const current = sorted[index];
    const swap = sorted[swapIndex];

    try {
      const [updatedCurrent, updatedSwap] = await Promise.all([
        taskService.updateTask(current.id, { sortOrder: swap.sortOrder }),
        taskService.updateTask(swap.id, { sortOrder: current.sortOrder }),
      ]);
      set((state) => ({
        tasks: state.tasks.map((t) => {
          if (t.id === current.id) return updatedCurrent;
          if (t.id === swap.id) return updatedSwap;
          return t;
        }),
        revision: state.revision + 1,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reorder task';
      console.error('TaskStore.reorderTask:', err);
      set({ error: message });
      throw err;
    }
  },

  snoozeTask: async (id, days) => {
    const { tasks } = get();
    const task = tasks.find((t) => t.id === id);
    if (!task) return;

    const baseDate = task.snoozedUntil ?? task.dueDate;
    const until = new Date(baseDate);
    until.setDate(until.getDate() + days);

    try {
      const updated = await taskService.snoozeTask(id, until);
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === id ? updated : t)),
        revision: state.revision + 1,
      }));
      posthogService.track(EVENT_TASK_SNOOZED);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to snooze task';
      console.error('TaskStore.snoozeTask:', err);
      set({ error: message });
      throw err;
    }
  },

  snoozeTaskUntil: async (id, until) => {
    try {
      const updated = await taskService.snoozeTask(id, until);
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === id ? updated : t)),
        revision: state.revision + 1,
      }));
      posthogService.track(EVENT_TASK_SNOOZED);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to snooze task';
      console.error('TaskStore.snoozeTaskUntil:', err);
      set({ error: message });
      throw err;
    }
  },

  unsnoozeTask: async (id) => {
    try {
      const updated = await taskService.updateTask(id, { snoozedUntil: undefined });
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === id ? updated : t)),
        revision: state.revision + 1,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to unsnooze task';
      console.error('TaskStore.unsnoozeTask:', err);
      set({ error: message });
      throw err;
    }
  },
}));
